import React, { useState, useEffect, useRef } from "react";
import styles from "./FilterDropdown.module.css";

interface FilterItem {
  id: string;
  label: string;
}

interface FilterDropdownProps {
  items: FilterItem[];
  onFilterChange: (selectedIds: string[]) => void;
  placeholder?: string;
  label?: string;
  FiterLabel: string;
  active?: boolean;
  initialSelectedIds?: string[];
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  items,
  onFilterChange,
  placeholder = "Select items...",
  FiterLabel,
  active,
  initialSelectedIds,
}) => {
  const isMonthFilter = FiterLabel.toLowerCase() === "month";
  const isYearFilter = FiterLabel.toLowerCase() === "year";

  const getCurrentMonthId = () => {
    const currentMonth = (new Date().getMonth() + 1).toString();
    return items.find((item) => item.id === currentMonth)?.id || "";
  };

  const getCurrentYearId = () => {
    const currentYear = new Date().getFullYear().toString();
    return items.find((item) => item.label === currentYear)?.id || items[0]?.id;
  };

  const initialSelectedItems = initialSelectedIds?.length
    ? initialSelectedIds
    : isMonthFilter
    ? [getCurrentMonthId()].filter(Boolean)
    : isYearFilter
    ? [getCurrentYearId()].filter(Boolean)
    : items.map((item) => item.id);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] =
    useState<string[]>(initialSelectedItems);
  const [tempSelectedItems, setTempSelectedItems] =
    useState<string[]>(initialSelectedItems);
  const hasLargeContent = items.some((item) => item.label.length > 20);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setTempSelectedItems(selectedItems);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedItems]);

  useEffect(() => {
    onFilterChange(selectedItems);
  }, [selectedItems, onFilterChange]);

  useEffect(() => {
    if (initialSelectedIds?.length) {
      setSelectedItems(initialSelectedIds);
      setTempSelectedItems(initialSelectedIds);
    } else {
      const newSelectedItems = isMonthFilter
        ? [getCurrentMonthId()].filter(Boolean)
        : isYearFilter
        ? [getCurrentYearId()].filter(Boolean)
        : items.map((item) => item.id);
      setSelectedItems(newSelectedItems);
      setTempSelectedItems(newSelectedItems);
    }
  }, [items, initialSelectedIds, isMonthFilter, isYearFilter]);

  const handleItemToggle = (itemId: string) => {
    setTempSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (tempSelectedItems.length === items.length) {
      setTempSelectedItems([]);
    } else {
      setTempSelectedItems(items.map((item) => item.id));
    }
  };

  const handleApply = () => {
    setSelectedItems(tempSelectedItems);
    onFilterChange(tempSelectedItems);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedItems(selectedItems);
    setIsOpen(false);
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      setTempSelectedItems(selectedItems);
    }
    setIsOpen(!isOpen);
  };

  const getDisplayText = () => {
    if (isOpen) return placeholder;
    if (selectedItems.length === 0) return placeholder;
    if (selectedItems.length === items.length) return "All Selected";

    const maxDisplayedItems = 1;
    const displayedItems = selectedItems
      .slice(0, maxDisplayedItems)
      .map((id) => items.find((item) => item.id === id)?.label)
      .filter(Boolean);

    let result = displayedItems.join(", ");
    if (selectedItems.length > maxDisplayedItems) {
      result += "...";
    }

    if (result.length > 15) {
      result = result.substring(0, 20) + "...";
    }

    return result;
  };

  return (
    <div
      className={
        active ? styles.summaryFilterContainer : styles.filterContainer
      }
    >
      <div
        className={
          active ? styles.summaryFilterDropdown : styles.filterDropdown
        }
        ref={dropdownRef}
      >
        <label className={styles.filterLabel}>{FiterLabel}</label>
        <div
          className={`${styles.dropdownToggle} ${
            isOpen ? styles.dropdownToggleOpen : ""
          } ${selectedItems.length === 0 ? styles.dropdownToggleDisabled : ""}`}
          onClick={handleToggleDropdown}
        >
          {getDisplayText()}
          <span className={`${styles.caret} ${isOpen ? styles.caretOpen : ""}`}>
            ▼
          </span>
        </div>
        {isOpen && (
          <div
            className={`${styles.dropdownMenu} ${
              hasLargeContent ? styles.largeDropdownMenu : ""
            }`}
          >
            <div className={`${styles.dropdownItem} ${styles.selectAll}`}>
              <label>
                <input
                  type="checkbox"
                  checked={tempSelectedItems.length === items.length}
                  onChange={handleSelectAll}
                />
                Select All
              </label>
            </div>
            {items.map((item) => (
              <div key={item.id} className={styles.dropdownItem}>
                <label>
                  <input
                    type="checkbox"
                    checked={tempSelectedItems.includes(item.id)}
                    onChange={() => handleItemToggle(item.id)}
                  />
                  {item.label}
                </label>
              </div>
            ))}
            <div className={styles.dropdownActions}>
              <button
                className={styles.applyButton}
                onClick={handleApply}
                disabled={tempSelectedItems.length === 0}
              >
                Apply
              </button>
              <button className={styles.cancelButton} onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdown;
